import { every, isNull, isUndefined, omit, sortedIndexBy, uniq } from "lodash";

import { AgentAssignmentFilterEnum, Filter, IFilters } from "constants/filters";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { SESSION_STORAGE_DRAFT_KEY } from "constants/tickets/drafts";
import { isTicketSatisfyingFilters } from "helpers/tickets";
import { ISimilarTicket, ITicket, TicketSilo, TicketStatus } from "interfaces/entities/ticket";
import {
  ITicketSorting,
  TicketSortingField,
  TicketSortingOrder,
  mapTicketSortingFieldToTicketField,
} from "interfaces/sorting";
import { getItem } from "services/session-storage";
import { CRUDAction, IEntityDelete, RequestAction } from "store/entities/actions";
import { IAgent } from "store/entities/agents";
import { CustomViewsActionsNames } from "store/entities/custom-views/actions";
import { IEntityCollectionState } from "store/entities/reducer";
import { IDeleteTagSuccessPayload, TAG, TagActionNames } from "store/entities/tags";
import { TICKET } from "store/entities/tickets/actions";
import {
  IFetchRecentTicketsSuccessPayload,
  IFetchSimilarTicketsSuccessPayload,
  IFetchTagSuggestionsSuccessPayload,
  IFetchTicketsToMergeSuccessPayload,
} from "store/entities/tickets/interfaces";
import { IActionWithPayload } from "store/helper";
import { TicketsViewAction, TicketsViewActionsNames } from "store/views/tickets/actions";
import { defaultFiltersOrder } from "store/views/tickets/helpers";

import {
  ITicketsViewClearDraftPayload,
  ITicketsViewDeleteTicketPayload,
  ITicketsViewFetchOriginalMessageSuccess,
  ITicketsViewFetchUnstrippedMessageSuccess,
  ITicketsViewNavigationItemCount,
  ITicketsViewSaveDraftPayload,
  ITicketsViewSendMessageSuccessPayload,
} from "./interfaces";

export interface ITicketsViewState {
  anyDataFetched: boolean;
  data: IEntityCollectionState<ITicket>;
  dataFetchedForRoute: TicketsRoutes | null;
  currentPage: number;
  totalPages: number;
  totalResults: number;
  current: ITicket | null;
  recentTickets: ITicket[] | null;
  suggestedTagIds: string[] | null;
  similarTickets: ISimilarTicket[] | null;
  ticketsToMerge: ITicket[];
  filters: IFilters;
  filtersOrder: Filter[];
  focusedColumn: number;
  selectedIds: string[];
  searchQuery: string;
  sendingMessage: boolean;
  sortedIds: string[];
  summarizingTickets?: { [key: string]: boolean };
  unspamming: boolean;
  silo: TicketSilo;
  route: TicketsRoutes;
  navigationItemCount: Partial<Record<TicketsRoutes, number>>;
  activeCustomViewId: string | null;
  currentSorting: ITicketSorting;
}

export const initialState: ITicketsViewState = {
  anyDataFetched: false,
  data: {},
  dataFetchedForRoute: null,
  currentPage: 1,
  // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'number'.
  totalPages: null,
  // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'number'.
  totalResults: null,
  current: null,
  recentTickets: null,
  suggestedTagIds: null,
  similarTickets: null,
  filters: {
    // @ts-expect-error TS(2322)
    agent: null,
    // @ts-expect-error TS(2322)
    agentAssignment: null,
    // @ts-expect-error TS(2322)
    creationDateRange: null,
    // @ts-expect-error TS(2322)
    dateRange: null,
    // @ts-expect-error TS(2322)
    group: null,
    // @ts-expect-error TS(2322)
    lastActivityDateRange: null,
    // @ts-expect-error TS(2322)
    lastMessageDateRange: null,
    // @ts-expect-error TS(2322)
    rating: null,
    // @ts-expect-error TS(2322)
    ratingDateRange: null,
    // @ts-expect-error TS(2322)
    resolutionDateRange: null,
    // @ts-expect-error TS(2322)
    responseDateRange: null,
    // @ts-expect-error TS(2322)
    responseAuthor: null,
    // @ts-expect-error TS(2322)
    tag: null,
    // @ts-expect-error TS(2322)
    ticketStatus: null,
  },
  filtersOrder: [],
  focusedColumn: 0,
  selectedIds: [],
  searchQuery: "",
  silo: TicketSilo.Tickets,
  sendingMessage: false,
  sortedIds: [],
  unspamming: false,
  route: TicketsRoutes.All,
  navigationItemCount: {},
  activeCustomViewId: null,
  // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'ITicket[]'.
  ticketsToMerge: null,
  currentSorting: {
    field: TicketSortingField.LastMessageAt,
    order: TicketSortingOrder.Descending,
  },
};

const TICKETS_PER_PAGE = 40;

const decorateTicketWithDraft = (ticket: ITicket): ITicket => {
  const ticketDrafts = getItem(SESSION_STORAGE_DRAFT_KEY);
  const existingDraft = ticketDrafts?.[ticket.id];
  return { ...ticket, draft: existingDraft };
};

function updateTicket(
  state: ITicketsViewState,
  payload: ITicket,
  agent: IAgent,
  overwriteCurrent = false,
  incrementCounter = false
): ITicketsViewState {
  const { id } = payload;
  const appliesToCurrent = overwriteCurrent || (state.current && state.current.id === id);
  const current = appliesToCurrent ? { ...payload } : state.current;
  const currentSorting = state.currentSorting;

  const currentWithDraft = current ? decorateTicketWithDraft(current) : null;

  const recentTicketIDs = (state.recentTickets || []).map(t => t.id);
  const isPossiblyRecentTicket = !appliesToCurrent && recentTicketIDs.includes(payload.id);

  const shouldResetRecentTickets =
    (appliesToCurrent && payload.requester.email !== state.current?.requester.email) ||
    (recentTicketIDs.includes(payload.id) && payload.requester.email !== state.current?.requester.email) ||
    (!recentTicketIDs.includes(payload.id) &&
      payload.id !== state.current?.id &&
      payload.requester.email === state.current?.requester.email) ||
    (payload.id === state.current?.id && state.current?.requester.email !== payload.requester.email);

  let recentTickets = isPossiblyRecentTicket
    ? (state.recentTickets || []).map(t => (t.id === payload.id ? payload : t))
    : state.recentTickets;
  recentTickets = shouldResetRecentTickets ? null : recentTickets;
  const newState = { ...state, current: currentWithDraft, recentTickets };

  const wasSatisfyingFiltersBeforeUpdate = state.data[id]
    ? isTicketSatisfyingFilters(state.filters, state.data[id], agent)
    : false;
  const isSatisfyingFiltersAfterUpdate = isTicketSatisfyingFilters(
    state.filters,
    payload,
    agent,
    state.silo,
    state.route
  );

  if (!isSatisfyingFiltersAfterUpdate) {
    const newData = omit(newState.data, id);
    const newSelectedIds = newState.selectedIds.filter(selectedId => selectedId !== id);
    const newSortedIds = newState.sortedIds.filter(sortedId => sortedId !== id);
    const totalResults = wasSatisfyingFiltersBeforeUpdate ? state.totalResults - 1 : state.totalResults;

    return {
      ...newState,
      data: newData,
      selectedIds: newSelectedIds,
      sortedIds: newSortedIds,
      totalResults,
    };
  }

  const isTicketAdd = !wasSatisfyingFiltersBeforeUpdate && isSatisfyingFiltersAfterUpdate;

  const isChildTicket = payload.parentTicket !== null;

  if (isTicketAdd) {
    if (!isChildTicket && state.searchQuery === "" && state.currentPage === 1) {
      const newData = {
        ...newState.data,
        [id]: payload,
      };

      const sortField = mapTicketSortingFieldToTicketField[currentSorting.field] ?? "lastMessageAt";

      const sortedIdIndex = sortedIndexBy(
        newState.sortedIds,
        id,
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        ticketId => newData[ticketId][sortField] * (currentSorting.order === TicketSortingOrder.Descending ? -1 : 1)
      );

      // the updated ticket should be removed if it's definitely way earlier than the youngest ticket on the current page
      const shouldTicketBeRemoved =
        state.totalPages > 1 &&
        currentSorting.order === TicketSortingOrder.Ascending &&
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        newData[id][sortField] > newData[newState.sortedIds[0]][sortField];

      let newSortedIds = [
        ...newState.sortedIds.slice(0, sortedIdIndex).filter(sortedId => sortedId !== id),
        id,
        ...newState.sortedIds.slice(sortedIdIndex).filter(sortedId => sortedId !== id),
      ];

      if (shouldTicketBeRemoved) {
        newSortedIds = newSortedIds.filter(sortedId => sortedId !== id);
      }

      const totalPages = Math.ceil((newState.totalResults + 1 + 0.01) / TICKETS_PER_PAGE);

      if (newSortedIds.length > TICKETS_PER_PAGE) {
        newSortedIds = newSortedIds.slice(0, TICKETS_PER_PAGE);
      }

      return {
        ...newState,
        data: newData,
        totalResults: newState.totalResults + 1,
        totalPages,
        sortedIds: newSortedIds,
      };
    }

    if (state.currentPage !== 1 && state.searchQuery === "") {
      const totalPages = Math.ceil((newState.totalResults + 1 + 0.01) / TICKETS_PER_PAGE);

      return {
        ...newState,
        totalResults: newState.totalResults + (incrementCounter ? 1 : 0),
        totalPages,
      };
    }
  } else {
    const newData = {
      ...newState.data,
      [id]: decorateTicketWithDraft(payload),
    };

    const sortField = mapTicketSortingFieldToTicketField[currentSorting.field] ?? "lastMessageAt";

    const sortedIdIndex = sortedIndexBy(
      newState.sortedIds,
      id,
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      ticketId => newData[ticketId][sortField] * (currentSorting.order === TicketSortingOrder.Descending ? -1 : 1)
    );

    const shouldTicketBeRemoved =
      state.totalPages > 1 &&
      currentSorting.order === TicketSortingOrder.Ascending &&
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      newData[id][sortField] > newData[newState.sortedIds.at(-1)][sortField];

    let newSortedIds = uniq([
      ...newState.sortedIds.slice(0, sortedIdIndex).filter(sortedId => sortedId !== id),
      id,
      ...newState.sortedIds.slice(sortedIdIndex).filter(sortedId => sortedId !== id),
    ]);

    if (shouldTicketBeRemoved) {
      newSortedIds = newSortedIds.filter(sortedId => sortedId !== id);
    }

    return {
      ...newState,
      sortedIds: newSortedIds,
      data: newData,
    };
  }

  return newState;
}

export const ticketsViewReducer = (
  state: ITicketsViewState = initialState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: TicketsViewAction | any // handles TICKET actions as well
): ITicketsViewState => {
  switch (action.type) {
    case TicketsViewActionsNames.SET_CURRENT_ITEM: {
      return {
        ...state,
        current: action.payload.ticket,
      };
    }

    case TicketsViewActionsNames.SET_FOCUSED_COLUMN: {
      const { focusedColumn } = action.payload;
      return { ...state, focusedColumn };
    }

    case TicketsViewActionsNames.UPDATE_BATCH:
      return {
        ...state,
        dataFetchedForRoute: null,
      };

    case TicketsViewActionsNames.RESET_SELECTED_TICKETS:
      return {
        ...state,
        selectedIds: [],
      };

    case TicketsViewActionsNames.SELECT_TICKETS:
      return {
        ...state,
        selectedIds: action.payload.ticketIds,
      };

    case TicketsViewActionsNames.SET_DATA: {
      const { data, totalPages, totalResults } = action.payload;
      const dataToSet = data.reduce(
        // @ts-expect-error TS(7006): Parameter 'acc' implicitly has an 'any' type.
        (acc, ticket) => ({
          ...acc,
          [ticket.id]: ticket,
        }),
        {}
      );
      const sortedIds = data.reduce(
        // FIXME: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (acc: any, ticket: any) => (!acc.includes(ticket.id) ? [...acc, ticket.id] : acc),
        []
      );
      const selectedIds = state.selectedIds.filter(selectedId => dataToSet[selectedId] != null);

      return {
        ...state,
        anyDataFetched: true,
        data: dataToSet,
        dataFetchedForRoute: state.route,
        totalPages,
        totalResults,
        selectedIds,
        sortedIds,
      };
    }

    case TicketsViewActionsNames.SET_PAGE: {
      const { page } = action.payload;
      return {
        ...state,
        currentPage: page || 1,
        dataFetchedForRoute: null,
      };
    }

    case TicketsViewActionsNames.UPDATE_FILTER: {
      const filterUpdate = action.payload;
      const newFilters = {
        ...state.filters,
        [filterUpdate.name]: filterUpdate.value,
      };
      const activeCustomViewId = every(newFilters, filter => isNull(filter) || isUndefined(filter))
        ? null
        : state.activeCustomViewId;
      let newFiltersOrder;
      if (filterUpdate.value === null) {
        newFiltersOrder = state.filtersOrder.filter(filterName => filterName !== filterUpdate.name);
      } else if (!state.filtersOrder.some(filterName => filterName === filterUpdate.name)) {
        newFiltersOrder = [filterUpdate.name, ...state.filtersOrder];
      } else {
        newFiltersOrder = state.filtersOrder;
      }
      return {
        ...state,
        filters: newFilters,
        filtersOrder: newFiltersOrder,
        dataFetchedForRoute: null,
        activeCustomViewId,
      };
    }

    case TicketsViewActionsNames.SET_VIEW: {
      const { route, activeCustomViewId, filters, query, page, silo, sortBy, order } = action.payload;

      return {
        ...state,
        route,
        activeCustomViewId: activeCustomViewId || null,
        filters,
        filtersOrder: defaultFiltersOrder.filter(f => Boolean(filters[f])),
        searchQuery: query || "",
        silo: silo || TicketSilo.Tickets,
        currentPage: page || 1,
        dataFetchedForRoute: null,
        currentSorting: {
          field: sortBy || TicketSortingField.LastMessageAt,
          order: order || TicketSortingOrder.Descending,
        },
      };
    }

    case TicketsViewActionsNames.SELECT_CUSTOM_VIEW: {
      return {
        ...state,
        activeCustomViewId: action.payload.id,
      };
    }

    case TicketsViewActionsNames.SET_SILO: {
      return {
        ...state,
        silo: action.payload.silo,
      };
    }

    case TicketsViewActionsNames.CLEAR_SEARCH_QUERY:
      return {
        ...state,
        route: TicketsRoutes.All,
        activeCustomViewId: null,
        searchQuery: "",
        filters: {},
        filtersOrder: [],
        dataFetchedForRoute: null,
        currentPage: 1,
      };

    case TicketsViewActionsNames.SHOW_FROM_ALL_TEAMS: {
      const { route } = action.payload;

      const filters: IFilters = {};
      let filtersOrder: Filter[] = [];

      if (route === TicketsRoutes.Unassigned) {
        filters[Filter.TicketStatus] = [TicketStatus.Open];
        filters[Filter.AgentAssignment] = [AgentAssignmentFilterEnum.Unassigned];
        filtersOrder = [Filter.TicketStatus, Filter.AgentAssignment];
      } else if (route === TicketsRoutes.MyOpen) {
        filters[Filter.TicketStatus] = [TicketStatus.Open];
        filtersOrder = [Filter.TicketStatus];
      }

      return {
        ...state,
        filters,
        filtersOrder,
      };
    }

    case TicketsViewActionsNames.SET_NAVIGATION_ITEM_COUNT: {
      const { payload } = action as IActionWithPayload<string, ITicketsViewNavigationItemCount>;
      return {
        ...state,
        navigationItemCount: {
          ...state.navigationItemCount,
          [payload.route]: payload.count,
        },
      };
    }

    case TICKET[CRUDAction.FETCH_SINGLE][RequestAction.REQUEST]: {
      return { ...state, recentTickets: null, similarTickets: null, suggestedTagIds: null };
    }

    case TICKET[CRUDAction.FETCH_SINGLE][RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<
        string,
        { value: ITicket; agent: IAgent; overwriteCurrent: boolean; incrementCounter: boolean }
      >;
      return updateTicket(state, payload.value, payload.agent, payload.overwriteCurrent, payload.incrementCounter);
    }

    case TICKET.SUMMARIZE_TICKET[RequestAction.REQUEST]: {
      const { payload } = action as IActionWithPayload<string, { ticketId: string }>;
      const { ticketId } = payload;

      return {
        ...state,
        summarizingTickets: {
          ...state.summarizingTickets,
          [ticketId]: true,
        },
      };
    }

    case TICKET.SUMMARIZE_TICKET[RequestAction.SUCCESS]:
    case TICKET.SUMMARIZE_TICKET[RequestAction.FAILURE]: {
      const { payload } = action as IActionWithPayload<string, { ticketId: string }>;
      const { ticketId } = payload;

      return {
        ...state,
        summarizingTickets: {
          ...state.summarizingTickets,
          [ticketId]: false,
        },
      };
    }

    case TICKET.ADD_TAGS[RequestAction.SUCCESS]:
    case TICKET.UPDATE_TAGS[RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, { ticket: ITicket; agent: IAgent }>;
      return {
        ...state,
        ...updateTicket(state, payload.ticket, payload.agent, false),
        suggestedTagIds: state.suggestedTagIds
          ? state.suggestedTagIds.filter(tagId => !payload.ticket.tagIds.includes(tagId))
          : null,
      };
    }

    case TICKET.UPDATE_SUBJECT[RequestAction.SUCCESS]:
    case TICKET.UPDATE_REQUESTER[RequestAction.SUCCESS]:
    case TICKET.UPDATE_CCS[RequestAction.SUCCESS]:
    case TICKET.UPDATE_ASSIGNEE[RequestAction.SUCCESS]:
    case TICKET.UPDATE_ASSIGNED_TEAM[RequestAction.SUCCESS]:
    case TICKET.UPDATE_CUSTOM_FIELD[RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, { ticket: ITicket; agent: IAgent }>;
      return updateTicket(state, payload.ticket, payload.agent, false);
    }

    case TICKET.SEND_MESSAGE[RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, ITicketsViewSendMessageSuccessPayload>;
      return {
        ...updateTicket(state, payload.ticket, payload.agent, false),
        sendingMessage: false,
      };
    }

    case TICKET.CREATE[RequestAction.REQUEST]:
    case TICKET.SEND_MESSAGE[RequestAction.REQUEST]:
      return { ...state, sendingMessage: true };

    case TICKET.DELETE[RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, ITicketsViewDeleteTicketPayload>;
      const ticketId = payload.id;

      const newData = { ...state.data };
      delete newData[ticketId];

      return {
        ...state,
        data: newData,
        sortedIds: state.sortedIds ? state.sortedIds.filter((id: string) => id !== ticketId) : state.sortedIds,
        selectedIds: state.selectedIds ? state.selectedIds.filter((id: string) => id !== ticketId) : state.selectedIds,
        recentTickets: state.recentTickets ? state.recentTickets.filter(t => t.id !== ticketId) : state.recentTickets,
        similarTickets: state.similarTickets
          ? state.similarTickets.filter(t => t.id !== ticketId)
          : state.similarTickets,
      };
    }

    case TICKET.SEND_MESSAGE[RequestAction.FAILURE]:
    case TICKET.CREATE[RequestAction.SUCCESS]:
    case TICKET.CREATE[RequestAction.FAILURE]:
      return { ...state, sendingMessage: false };

    case TICKET.UNSPAM[RequestAction.REQUEST]:
      return { ...state, unspamming: true };

    case TICKET.UNSPAM[RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, { ticket: ITicket; agent: IAgent }>;
      return {
        ...updateTicket(state, payload.ticket, payload.agent, false),
        unspamming: false,
      };
    }

    case TICKET.UNSPAM[RequestAction.FAILURE]:
      return { ...state, unspamming: false };

    case TICKET.FETCH_RECENT_TICKETS[RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, IFetchRecentTicketsSuccessPayload>;
      const { ticketId, values: recentTickets } = payload;

      if (state.current?.id !== ticketId) {
        return state;
      }

      return {
        ...state,
        recentTickets,
      };
    }

    case TICKET.FETCH_TAG_SUGGESTIONS[RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, IFetchTagSuggestionsSuccessPayload>;
      const { ticketId, tagIds } = payload;

      if (state.current?.id !== ticketId) {
        return state;
      }

      return {
        ...state,
        suggestedTagIds: tagIds,
      };
    }

    case TICKET.FETCH_TAG_SUGGESTIONS[RequestAction.FAILURE]: {
      return {
        ...state,
        suggestedTagIds: [],
      };
    }

    case TICKET.FETCH_TAG_SUGGESTIONS[RequestAction.REQUEST]: {
      return {
        ...state,
        suggestedTagIds: null,
      };
    }

    case TAG[CRUDAction.DELETE][RequestAction.SUCCESS]:
    case TagActionNames.SYNC_DELETE: {
      const { id } = action.payload as IDeleteTagSuccessPayload;
     
      return {
        ...state,
        suggestedTagIds: state.suggestedTagIds?.filter(tagId => tagId !== id) ?? null,
      }
    }
    
    case TICKET.FETCH_SIMILAR_TICKETS[RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, IFetchSimilarTicketsSuccessPayload>;
      const { values: similarTickets } = payload;

      return {
        ...state,
        similarTickets,
      };
    }

    case TICKET.FETCH_TICKETS_TO_MERGE[RequestAction.SUCCESS]: {
      const { payload } = action as IActionWithPayload<string, IFetchTicketsToMergeSuccessPayload>;
      const { ticketId, values: ticketsToMerge } = payload;

      if (state.current?.id !== ticketId) {
        return state;
      }

      return {
        ...state,
        ticketsToMerge,
      };
    }

    case CustomViewsActionsNames.DELETE.SUCCESS: {
      const {
        payload: { id },
      } = action as IActionWithPayload<string, IEntityDelete>;
      if (state.activeCustomViewId === id) {
        return {
          ...state,
          activeCustomViewId: null,
          filters: {},
          filtersOrder: [],
        };
      }
      return state;
    }

    case TicketsViewActionsNames.FETCH_ORIGINAL_MESSAGE_SUCCESS: {
      const {
        payload: { eventId, ticketId, message },
      } = action as IActionWithPayload<string, ITicketsViewFetchOriginalMessageSuccess>;

      if (!state.current || state.current.id !== ticketId) {
        return state;
      }

      const events = state.current.events.map(event =>
        event.id === eventId ? { ...event, originalMessage: message } : event
      );

      return {
        ...state,
        current: {
          ...state.current,
          events,
        },
      };
    }

    case TicketsViewActionsNames.FETCH_UNSTRIPPED_MESSAGE_SUCCESS: {
      const {
        payload: { ticketId, message, eventId },
      } = action as IActionWithPayload<string, ITicketsViewFetchUnstrippedMessageSuccess>;
      if (!state.current || state.current.id !== ticketId) {
        return state;
      }

      const events = state.current.events.map(event =>
        event.id === eventId ? { ...event, uncutContent: message } : event
      );

      return {
        ...state,
        current: {
          ...state.current,
          events,
        },
      };
    }

    case TicketsViewActionsNames.UPDATE_CURRENT_SORTING: {
      const {
        payload: { field, order },
      } = action as IActionWithPayload<string, ITicketSorting>;
      return {
        ...state,
        currentSorting: {
          field,
          order,
        },
      };
    }

    case TicketsViewActionsNames.RESET_CURRENT_SORTING: {
      return {
        ...state,
        currentSorting: initialState.currentSorting,
      };
    }

    case TicketsViewActionsNames.SAVE_DRAFT: {
      const { payload } = action as IActionWithPayload<string, ITicketsViewSaveDraftPayload>;
      if (!state.current) {
        return state;
      }
      const isCurrentTicket = state.current.id === payload.ticketId;
      const { ticketId, ...draft } = payload;
      return {
        ...state,
        data: {
          ...state.data,
          [ticketId]: {
            ...state.data[ticketId],
            draft,
          },
        },
        current: isCurrentTicket ? { ...state.current, draft } : state.current,
      };
    }

    case TicketsViewActionsNames.CLEAR_DRAFT: {
      const { payload } = action as IActionWithPayload<string, ITicketsViewClearDraftPayload>;
      if (!state.current) {
        return state;
      }
      const isCurrentTicket = state.current.id === payload.ticketId;
      return {
        ...state,
        data: {
          ...state.data,
          [payload.ticketId]: {
            ...state.data[payload.ticketId],
            draft: undefined,
          },
        },
        current: isCurrentTicket ? { ...state.current, draft: undefined } : state.current,
      };
    }

    default:
      return state;
  }
};
